import * as Yup from 'yup';
import { Button, FormSelectField, Grid, Typography } from 'ui';
import { Form, Formik } from 'formik';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { gql } from '@apollo/client';
import {
  useCreateJceHomepageOrderingEntryMutation,
  useGetCollectionsForGeographyQuery,
  useSetJceHomepageEntriesForGeographyMutation,
} from 'generated/graphql';
import { useNavigate } from 'react-router-v7';
import ConfirmDialog from 'ui/ConfirmDialog/ConfirmDialog';
import React, { useCallback, useEffect, useState } from 'react';

import {
  CollectionSummary,
  OrderingEntrySummary,
  SPECIAL_COLLECTION_OPTIONS,
  SpecialCollectionValue,
} from './rowSummaryTypes';
import {
  DraggableOrderingRow,
  DraggableUnorderedRow,
  OrderingEntriesEmptyDropZone,
  OrderingEntriesTrailingDropZone,
  UnorderedCollectionsEmptyDropZone,
} from './DraggableRows';
import { getCollectionsForGeographyRefetch } from './queries';
import EditCollectionButton from './EditCollectionButton';

// ─── GraphQL documents ────────────────────────────────────────────────────────

gql`
  mutation CreateJceHomepageOrderingEntry($homepageOrderingEntry: JceHomepageOrderingEntryInput!) {
    createJceHomepageOrderingEntry(homepageOrderingEntry: $homepageOrderingEntry) {
      id
      position
      specialCollection
      collection {
        id
        name
      }
    }
  }
`;

gql`
  mutation SetJceHomepageEntriesForGeography(
    $geographyId: Int!
    $orderingEntries: [JceHomepageOrderingEntryInput!]!
  ) {
    setJceHomepageOrderingEntriesForGeography(
      geographyId: $geographyId
      orderingEntries: $orderingEntries
    ) {
      id
      position
      specialCollection
      isDesignatedForLeaderboard
      collection {
        id
        name
      }
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderingEntryMode = 'collection' | 'special';

interface CreateOrderingEntryValues {
  mode: OrderingEntryMode;
  collectionId: string | '';
  specialCollection: SpecialCollectionValue | '';
}

// ─── Validation schema ────────────────────────────────────────────────────────

const createOrderingEntrySchema = Yup.object({
  mode: Yup.string().oneOf(['collection', 'special']).required(),
  collectionId: Yup.string().when('mode', {
    is: 'collection',
    then: (s) => s.required('A collection must be selected'),
    otherwise: (s) => s.optional(),
  }),
  specialCollection: Yup.string().when('mode', {
    is: 'special',
    then: (s) => s.required('A special collection type must be selected'),
    otherwise: (s) => s.optional(),
  }),
});

// ─── JceGeographyDetail ───────────────────────────────────────────────────────
interface JceGeographyDetailProps {
  geographyId: string;
}

const JceGeographyDetail: React.FC<JceGeographyDetailProps> = ({ geographyId }) => {
  const geographyIdNum = Number(geographyId);
  const navigate = useNavigate();

  const { data } = useGetCollectionsForGeographyQuery({
    variables: { geographyId: geographyIdNum },
  });

  const [createOrderingEntry] = useCreateJceHomepageOrderingEntryMutation();
  const [setOrderingEntries, { loading: isSaving }] =
    useSetJceHomepageEntriesForGeographyMutation();

  /** Persist an arbitrary ordered list of entries for this geography. */
  const saveOrderingEntries = useCallback(
    (entries: OrderingEntrySummary[]) =>
      setOrderingEntries({
        variables: {
          geographyId: geographyIdNum,
          orderingEntries: entries.map((e) => ({
            geographyId: geographyIdNum,
            collectionId: e.collection?.id,
            specialCollection: e.specialCollection,
            isDesignatedForLeaderboard: e.isDesignatedForLeaderboard ?? false,
          })),
        },
        refetchQueries: getCollectionsForGeographyRefetch(geographyIdNum),
        awaitRefetchQueries: true,
      }),
    [geographyIdNum, setOrderingEntries]
  );

  const serverOrderingEntries: OrderingEntrySummary[] =
    (data?.collectionsForGeography?.orderingEntries as OrderingEntrySummary[] | undefined) ?? [];
  const unorderedCollections: CollectionSummary[] =
    (data?.collectionsForGeography?.unorderedCollections as CollectionSummary[] | undefined) ?? [];

  // Local drag state — seeded from server; reset whenever server data changes.
  const [orderedEntries, setOrderedEntries] = useState<OrderingEntrySummary[]>([]);
  useEffect(() => {
    setOrderedEntries(serverOrderingEntries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Confirm dialog state for dragging a special ordering entry to unordered (= remove)
  const [pendingRemoveEntry, setPendingRemoveEntry] = useState<OrderingEntrySummary | null>(null);

  // All collections that already appear in an ordering entry — used to prevent duplicates.
  const usedCollectionIds = new Set(
    serverOrderingEntries.flatMap((e) => (e.collection ? [String(e.collection.id)] : []))
  );

  // Collections available to add as a new ordering entry (not yet ordered).
  const unorderedOnlyCollections = unorderedCollections.filter(
    (col) => !usedCollectionIds.has(String(col.id))
  );

  // Special collections already used as ordering entries.
  const usedSpecialCollections = new Set(
    serverOrderingEntries.map((e) => e.specialCollection).filter(Boolean) ?? []
  );
  const availableSpecialCollections = SPECIAL_COLLECTION_OPTIONS.filter(
    (sc) => !usedSpecialCollections.has(sc)
  );

  const handleMoveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedEntries((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      return next;
    });
  }, []);

  const handleDropRow = useCallback(async () => {
    await saveOrderingEntries(orderedEntries);
  }, [orderedEntries, saveOrderingEntries]);

  const handleRemoveEntry = useCallback(
    async (entryId: number) => {
      const remaining = orderedEntries.filter((e) => e.id !== entryId);
      await saveOrderingEntries(remaining);
    },
    [orderedEntries, saveOrderingEntries]
  );

  const handleLeaderboardChange = useCallback(
    async (entryId: number, value: boolean) => {
      const updated = orderedEntries.map((e) => {
        if (e.id === entryId) {
          return { ...e, isDesignatedForLeaderboard: value };
        } else if (value) {
          // When designating a new entry, clear all others.
          return { ...e, isDesignatedForLeaderboard: false };
        } else {
          return e;
        }
      });
      setOrderedEntries(updated);
      await saveOrderingEntries(updated);
    },
    [orderedEntries, saveOrderingEntries]
  );

  const handleDropCollectionIntoOrdering = useCallback(
    async (collectionSummary: CollectionSummary, atIndex: number) => {
      const newEntry: OrderingEntrySummary = {
        id: -collectionSummary.id,
        position: atIndex + 1,
        specialCollection: null,
        collection: collectionSummary,
      };
      const next = [...orderedEntries];
      next.splice(atIndex, 0, newEntry);
      setOrderedEntries(next);
      await saveOrderingEntries(next);
    },
    [orderedEntries, saveOrderingEntries]
  );

  const handleDropOrderingIntoUnordered = useCallback(
    (entry: OrderingEntrySummary) => {
      if (entry.specialCollection) {
        setPendingRemoveEntry(entry);
      } else {
        void handleRemoveEntry(entry.id);
      }
    },
    [handleRemoveEntry]
  );

  const convertCollectionId = (collectionId: string): number | null =>
    collectionId === '' ? null : Number(collectionId);
  const convertSpecialCollection = (
    specialCollection: SpecialCollectionValue | ''
  ): SpecialCollectionValue | null => (specialCollection === '' ? null : specialCollection);

  const handleCreateOrderingEntry = async (values: CreateOrderingEntryValues) => {
    await createOrderingEntry({
      variables: {
        homepageOrderingEntry: {
          geographyId: geographyIdNum,
          collectionId:
            values.mode === 'collection' ? convertCollectionId(values.collectionId) : null,
          specialCollection:
            values.mode === 'special' ? convertSpecialCollection(values.specialCollection) : null,
        },
      },
      refetchQueries: getCollectionsForGeographyRefetch(geographyIdNum),
    });
  };

  return (
    <>
      {/* ── Ordering Entries ── */}
      <Typography variant="heading2Medium">Ordering Entries</Typography>
      {orderedEntries.length === 0 ? (
        <OrderingEntriesEmptyDropZone
          onDrop={(collectionSummary) => handleDropCollectionIntoOrdering(collectionSummary, 0)}
        />
      ) : (
        <TableContainer style={{ marginBottom: 32 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 32 }} />
                <TableCell>Position</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Leaderboard</TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedEntries.map((entry, i) => (
                <DraggableOrderingRow
                  key={entry.id}
                  entry={entry}
                  index={i}
                  isSaving={isSaving}
                  editButton={
                    entry.collection ? (
                      <EditCollectionButton
                        collectionSummary={entry.collection}
                        geographyId={geographyId}
                        disabled={isSaving}
                      />
                    ) : undefined
                  }
                  onMove={handleMoveRow}
                  onDrop={handleDropRow}
                  onRemove={() => handleRemoveEntry(entry.id)}
                  onDropUnordered={(col) => handleDropCollectionIntoOrdering(col, i)}
                  onLeaderboardChange={(value) => handleLeaderboardChange(entry.id, value)}
                />
              ))}
              <OrderingEntriesTrailingDropZone
                colSpan={7}
                onDrop={(col) => handleDropCollectionIntoOrdering(col, orderedEntries.length)}
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Add Ordering Entry ── */}
      <Typography variant="heading2Medium" mb={3}>
        Add Ordering Entry
      </Typography>
      <Formik<CreateOrderingEntryValues>
        initialValues={{ mode: 'collection', collectionId: '', specialCollection: '' }}
        validationSchema={createOrderingEntrySchema}
        onSubmit={async (values, { resetForm }) => {
          await handleCreateOrderingEntry(values);
          resetForm();
        }}
      >
        {({ values, setFieldValue, dirty, isValid, isSubmitting }) => (
          <Form>
            <Grid container>
              <Grid item xs={3}>
                <FormSelectField
                  label="Type"
                  name="mode"
                  value={values.mode}
                  onChange={(e) => {
                    setFieldValue('mode', e.target.value);
                    setFieldValue('collectionId', '');
                    setFieldValue('specialCollection', '');
                  }}
                >
                  <option value="collection">Existing Collection</option>
                  <option value="special">Special Collection</option>
                </FormSelectField>
              </Grid>
              <Grid item xs={3}>
                {values.mode === 'collection' && (
                  <FormSelectField
                    fullWidth
                    name="collectionId"
                    label="Collection"
                    value={values.collectionId}
                    onChange={(e) => setFieldValue('collectionId', e.target.value)}
                  >
                    <option key={0} value="" />
                    {unorderedOnlyCollections.map((col) => (
                      <option key={col.id} value={String(col.id)}>
                        {col.name} (id: {col.id})
                      </option>
                    ))}
                  </FormSelectField>
                )}
                {values.mode === 'special' && (
                  <FormSelectField
                    fullWidth
                    name="specialCollection"
                    label="Special Collection"
                    value={values.specialCollection}
                    onChange={(e) => setFieldValue('specialCollection', e.target.value)}
                  >
                    <option value="" />
                    {availableSpecialCollections.map((sc) => (
                      <option key={sc} value={sc}>
                        {sc}
                      </option>
                    ))}
                  </FormSelectField>
                )}
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={2}>
                <Button
                  color="primary"
                  disabled={!dirty || !isValid || isSubmitting}
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Add Entry
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>

      {/* ── Unordered Collections ── */}
      <Typography variant="heading2Medium">Unordered Collections</Typography>
      {unorderedCollections.length === 0 ? (
        <UnorderedCollectionsEmptyDropZone onDrop={handleDropOrderingIntoUnordered} />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 32 }} />
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {unorderedCollections.map((collectionSummary) => (
                <DraggableUnorderedRow
                  key={collectionSummary.id}
                  collectionSummary={collectionSummary}
                  isSaving={isSaving}
                  onDropOrdering={handleDropOrderingIntoUnordered}
                  editButton={
                    <EditCollectionButton
                      collectionSummary={collectionSummary}
                      geographyId={geographyId}
                    />
                  }
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Button
        size="medium"
        variant="contained"
        color="primary"
        style={{ marginTop: 20 }}
        onClick={() => navigate(`/jce-homepage-geography/${geographyId}/collections/create`)}
      >
        + New Collection
      </Button>

      <ConfirmDialog
        open={pendingRemoveEntry !== null}
        isDestructive
        title="Remove special collection?"
        continueText="Remove"
        onCancel={() => setPendingRemoveEntry(null)}
        onContinue={() => {
          if (pendingRemoveEntry) {
            void handleRemoveEntry(pendingRemoveEntry.id);
          }
          setPendingRemoveEntry(null);
        }}
      >
        {`"${pendingRemoveEntry?.specialCollection}" is a special collection. Dragging it to Unordered Collections will remove it from the ordering entirely. Are you sure?`}
      </ConfirmDialog>
    </>
  );
};

export default JceGeographyDetail;
